// src/pages/MedicoProfilePage.jsx

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axiosInstance from '../axiosInstance';
import { toast } from 'react-toastify';
import {
  Paper, Box, Typography, Button, TextField, CircularProgress, Divider, Avatar,
  FormControl, InputLabel, Select, MenuItem, Grid, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Backdrop, IconButton, Card, CardContent, Chip, Fade,
  Autocomplete, InputAdornment, alpha, useTheme, Link, Alert, useMediaQuery,
  FormControlLabel, Switch
} from '@mui/material';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import EditIcon from '@mui/icons-material/Edit';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SchoolIcon from '@mui/icons-material/School';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import countriesData from '../data/countries.json';
import { useCountryCodes } from '../hooks/useCountryCodes';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import PublicIcon from '@mui/icons-material/Public';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import RateReviewIcon from '@mui/icons-material/RateReview'
import PaymentIcon from '@mui/icons-material/Payment';
import { motion } from 'framer-motion';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://app-medifyhc.com';

// Regex para validaciones simples
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneRegex = /^\+?[0-9\-\s]{7,20}$/;

// Monedas extendidas (3 letras ISO)
const currencyOptions = [
  'USD', 'EUR', 'MXN', 'HNL', 'COP', 'PEN', 'GTQ',
  'ARS', 'BRL', 'CLP', 'CRC', 'DOP', 'BOB', 'UYU', 'PYG',
  'CAD', 'GBP', 'JPY', 'CNY', 'AUD', 'CHF', 'VES', 'NIO', 'SVC', 'PAB',
  'BZD', 'JMD', 'ILS', 'SEK', 'NOK', 'DKK', 'ZAR', 'INR'
];

const constructImageUrl = (imagePath) => {
  if (!imagePath) return null;

  // If the path already contains the full URL, return it as is
  if (imagePath.startsWith('http')) {
    return imagePath;
  }
  let cleanPath = imagePath;
  if (cleanPath.includes(API_BASE_URL)) {
    cleanPath = cleanPath.replace(API_BASE_URL, '');
  }
  cleanPath = cleanPath.startsWith('/') ? cleanPath.substring(1) : cleanPath;
  const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL : `${API_BASE_URL}/`;
  return `${baseUrl}${cleanPath}`;
};

// --- Helpers de normalización para inputs controlados ---
const safeStr = (v) => (v ?? ''); // Nunca null/undefined para inputs controlados

const extractPhone = (raw = '', phoneCodes = [], defaultCode = '+504') => {
  let code = defaultCode;
  let num = raw || '';
  for (const pc of phoneCodes) {
    if (num.startsWith(pc.code)) {
      code = pc.code;
      num = num.substring(pc.code.length).trim();
      break;
    }
  }
  return { code, num };
};

const MotionPaper = motion(Paper);
const normalizeProfileData = (user, phoneCodes) => {
  const { code: celCode, num: celular } = extractPhone(user?.medico?.celular || '', phoneCodes, '+504');
  const { code: consCode, num: telefono_consultorio } = extractPhone(user?.medico?.telefono_consultorio || '', phoneCodes, '+504');

  return {
    data: {
      first_name: safeStr(user?.first_name),
      last_name: safeStr(user?.last_name),
      email: safeStr(user?.email),
      genero: safeStr(user?.medico?.genero),
      especialidad_principal: safeStr(user?.medico?.especialidad_principal),
      sub_especialidad: safeStr(user?.medico?.sub_especialidad),
      numero_colegiado: safeStr(user?.medico?.numero_colegiado),
      universidad: safeStr(user?.medico?.universidad),
      pais: safeStr(user?.medico?.pais),
      estado_provincia: safeStr(user?.medico?.estado_provincia),
      ciudad: safeStr(user?.medico?.ciudad),
      direccion: safeStr(user?.medico?.direccion),
      celular: safeStr(celular),
      telefono_consultorio: safeStr(telefono_consultorio),
      direccion_consultorio: safeStr(user?.medico?.direccion_consultorio),
      sobre_mi: safeStr(user?.medico?.sobre_mi),
      foto_perfil: user?.medico?.foto_perfil || null,
      instagram: safeStr(user?.medico?.instagram),
      facebook: safeStr(user?.medico?.facebook),
      tiktok: safeStr(user?.medico?.tiktok),
      linkedin: safeStr(user?.medico?.linkedin),
      firma_correo: safeStr(user?.medico?.firma_correo),
      idiomas: Array.isArray(user?.medico?.idiomas) ? user.medico.idiomas : [],
      logo_clinica: user?.medico?.logo_clinica || null,
    },
    celCode,
    consCode,
    previewImg: constructImageUrl(user?.medico?.foto_perfil) || null,
    previewFirma: user?.medico?.firma_digital ? constructImageUrl(user.medico.firma_digital) : null,
    previewSello: user?.medico?.sello_digital ? constructImageUrl(user.medico.sello_digital) : null,
    previewLogoClinica: user?.medico?.logo_clinica ? constructImageUrl(user.medico.logo_clinica) : null,
  };
};

const MedicoProfilePage = () => {
  const { user, refreshAuthData } = useAuth();
  const theme = useTheme(); // Obtener el tema actual
  const isDarkMode = theme.palette.mode === 'dark';
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
     
     // Check if user is a doctor, redirect if not
     const isDoctor = !!user?.medico?.id_medico;
     
     useEffect(() => {
       if (user && !isDoctor) {
         toast.error('Esta página está disponible solo para usuarios con rol de médico.');
         navigate('/account/perfil', { replace: true });
       }
     }, [user, isDoctor, navigate]);
  // Códigos telefónicos de países
  const { countryCodes: phoneCodes, loading: phoneCodesLoading } = useCountryCodes();
  const [phoneCodeSearch, setPhoneCodeSearch] = useState('');
  const [firmaDigitalFile, setFirmaDigitalFile] = useState(null);
  const [previewFirmaDigital, setPreviewFirmaDigital] = useState(null);
  const [selloDigitalFile, setSelloDigitalFile] = useState(null);
  const [previewSelloDigital, setPreviewSelloDigital] = useState(null);
  const [logoClinicaFile, setLogoClinicaFile] = useState(null);
  const [previewLogoClinica, setPreviewLogoClinica] = useState(null);

  // --- Declaración de Estados ---
  const [isEditing, setIsEditing] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profilePicFile, setProfilePicFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);

  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [stateLoading, setStateLoading] = useState(false);

  // Estados para códigos telefónicos
  const [celularCode, setCelularCode] = useState('+504'); // Honduras por defecto
  const [consultorioCode, setConsultorioCode] = useState('+504'); // Honduras por defecto

  const [errors, setErrors] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Estado para animaciones
  const [fadeIn, setFadeIn] = useState(false);

  /// --- Efectos (Side Effects) ---

// Inicializa el formulario con los datos del usuario del contexto
  useEffect(() => {
    if (user && phoneCodes.length > 0) {
      const norm = normalizeProfileData(user, phoneCodes);
      setCelularCode(norm.celCode);
      setConsultorioCode(norm.consCode);
      setProfileData(norm.data);
      setPreviewImage(norm.previewImg);
      setPreviewFirmaDigital(norm.previewFirma);
      setPreviewSelloDigital(norm.previewSello);
      setPreviewLogoClinica(norm.previewLogoClinica);
      setLoading(false);
      setTimeout(() => setFadeIn(true), 100);
    }
  }, [user, phoneCodes]);

  const handleCancel = () => {
    if (!user) return;
    const norm = normalizeProfileData(user, phoneCodes);
    setProfileData(norm.data);
    setPreviewImage(norm.previewImg);
    setProfilePicFile(null);
    setFirmaDigitalFile(null);
    setPreviewFirmaDigital(norm.previewFirma);
    setCelularCode(norm.celCode);
    setConsultorioCode(norm.consCode);
    setErrors({});
    setIsEditing(false);
  };


  useEffect(() => {
    const validCountries = countriesData.filter(c => c && c.code2);
    const sortedCountries = validCountries.sort((a, b) => a.name.localeCompare(b.name));
    setCountries(sortedCountries);
  }, []);

  // Carga los estados/provincias del país seleccionado desde el archivo local
  useEffect(() => {
    if (profileData?.pais) {
      setStateLoading(true);
      const selectedCountry = countries.find(c => c.name === profileData.pais);
      const countryStates = selectedCountry?.states || [];
      const sortedStates = countryStates.sort((a, b) => a.name.localeCompare(b.name));
      setStates(sortedStates);
      setStateLoading(false);
    } else {
      setStates([]);
    }
  }, [profileData?.pais, countries]);

  // --- Configuración de pago ---
  const [paymentConfig, setPaymentConfig] = useState({
    cuenta_paypal: '',
    tarifa_consulta: 0,
    moneda: 'USD',
    permitir_agendamiento_online: true,
  });
  const [loadingPayConfig, setLoadingPayConfig] = useState(true);
  const [savingPayConfig, setSavingPayConfig] = useState(false);

  useEffect(() => {
    // Only fetch payment config if user is a doctor
    if (!isDoctor) { setLoadingPayConfig(false); return; }

    
    const ctrl = new AbortController();

    const fetchPayConfig = async () => {
      setLoadingPayConfig(true);
      try {
        const { data } = await axiosInstance.get('/medicos/configuracion-pago/', {
          signal: ctrl.signal,
          params: { t: Date.now() } // evitar caché (opcional)
        });
        setPaymentConfig({
          cuenta_paypal: data.cuenta_paypal || '',
          tarifa_consulta: data.tarifa_consulta ?? 0,
          moneda: data.moneda || 'USD',
          permitir_agendamiento_online: data.permitir_agendamiento_online ?? true,
        });
      } catch (e) {
        if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') return;
        const st = e?.response?.status;
        if (st === 404 || st === 403) {
          // No aplicable a este usuario: ignora silenciosamente
          return;
        }
        // Cualquier otro error real 
        toast.error('No se pudo cargar la configuración de pago.');
      } finally {
        setLoadingPayConfig(false);
      }
    };

    fetchPayConfig();
    return () => ctrl.abort();
  }, [user, isDoctor]);

  const handleSavePayConfig = async () => {
    const invalidEmail = paymentConfig.cuenta_paypal && !emailRegex.test(paymentConfig.cuenta_paypal);
    if (invalidEmail) { toast.warn('El correo de PayPal no es válido.'); return; }
    if (!(Number(paymentConfig.tarifa_consulta) > 0)) { toast.warn('Ingresa un precio de consulta mayor a 0.'); return; }
    if (!paymentConfig.moneda || paymentConfig.moneda.length !== 3) { toast.warn('Selecciona una moneda válida.'); return; }

    setSavingPayConfig(true);
    try {
      await axiosInstance.put('/medicos/configuracion-pago/', {
        cuenta_paypal: paymentConfig.cuenta_paypal || null, // para backend puedes enviar null
        tarifa_consulta: Number(paymentConfig.tarifa_consulta),
        moneda: paymentConfig.moneda,
        permitir_agendamiento_online: paymentConfig.permitir_agendamiento_online,
      });
      toast.success('Configuración de pagos actualizada');
      await refreshAuthData?.();
    } catch (e) {
      if (e?.response?.status === 404) {
        toast.error('Endpoint de configuración no encontrado en el servidor.');
      } else {
        toast.error('No se pudo guardar la configuración de pagos');
      }
    } finally {
      setSavingPayConfig(false);
    }
  };

  // --- Manejadores de Eventos ---

  // Añadir manejador para el cambio de archivo de firma
  const handleFirmaDigitalChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Verificar el tamaño del archivo (limitar a 1MB)
      if (file.size > 1024 * 1024) {
        toast.warn('La imagen de firma es demasiado grande. Por favor, use una imagen más pequeña (máximo 1MB).');
        return;
      }

      setFirmaDigitalFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewFirmaDigital(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSelloDigitalChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 1024 * 1024) { // 1MB
        toast.warn('La imagen del sello es demasiado grande. Máximo 1MB.');
        return;
      }
      setSelloDigitalFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setPreviewSelloDigital(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleLogoClinicaChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 1024 * 1024) { // 1MB
        toast.warn('El logo de clínica es demasiado grande. Máximo 1MB.');
        return;
      }
      setLogoClinicaFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setPreviewLogoClinica(reader.result);
      reader.readAsDataURL(file);
    }
  };


  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({ ...prev, [name]: value }));
    if (name === 'email') setErrors(prev => ({ ...prev, email: emailRegex.test(value) ? '' : 'Correo inválido' }));
    if (name === 'celular') setErrors(prev => ({ ...prev, celular: phoneRegex.test(value) ? '' : 'Número no válido' }));
  };

  const handleCountryChange = (e, newValue) => {
    setProfileData(prev => ({
      ...prev,
      pais: newValue?.name || '',
      estado_provincia: ''
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setProfilePicFile(file);
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  const handleSave = () => {
    if (errors.email || errors.celular) {
      toast.warn('Por favor, corrige los campos marcados en rojo.');
      return;
    }
    setConfirmOpen(true);
  };

  const confirmSave = async () => {
    setConfirmOpen(false);
    setSaving(true);
    const medicoId = user?.medico?.id_medico;

    if (!medicoId) {
      toast.error("No se encontró el ID del perfil médico.");
      setSaving(false);
      return;
    }

    const userPayload = {
      first_name: profileData.first_name,
      last_name: profileData.last_name,
      email: profileData.email,
    };

    // Formatear números de teléfono con códigos de país
    const formattedCelular = `${celularCode}${profileData.celular}`;
    const formattedConsultorio = `${consultorioCode}${profileData.telefono_consultorio}`;

    try {
      // 1) Actualiza datos del usuario
      await axiosInstance.patch('/auth/user/', userPayload);

      // 2) Arma el FormData del médico
      const medicoPayload = new FormData();
      const medicoFields = [
        'especialidad_principal',
        'sub_especialidad',
        'genero',
        'numero_colegiado',
        'universidad',
        'pais',
        'estado_provincia',
        'ciudad',
        'direccion',
        'direccion_consultorio',
        'sobre_mi',
      ];
      medicoFields.forEach((field) => {
        medicoPayload.append(field, profileData?.[field] || '');
      });

      // Teléfonos con código
      medicoPayload.append('celular', formattedCelular);
      medicoPayload.append('telefono_consultorio', formattedConsultorio);

      // Redes sociales
      medicoPayload.append('instagram', profileData.instagram || '');
      medicoPayload.append('facebook', profileData.facebook || '');
      medicoPayload.append('tiktok', profileData.tiktok || '');
      medicoPayload.append('linkedin', profileData.linkedin || '');

      // Firma correo
      medicoPayload.append('firma_correo', profileData.firma_correo || '');

      // Foto de perfil
      if (profilePicFile) {
        medicoPayload.append('foto_perfil', profilePicFile);
      }

      // Firma digital
      if (firmaDigitalFile) {
        medicoPayload.append('firma_digital', firmaDigitalFile);
      }

      if (selloDigitalFile) {
        medicoPayload.append('sello_digital', selloDigitalFile);
      }
      if (logoClinicaFile) {
        medicoPayload.append('logo_clinica', logoClinicaFile);
      }

      // IDIOMAS — añadir después de crear medicoPayload
      const idiomasToSend = (profileData.idiomas || [])
        .slice(0, 2)
        .map((v) => (v || '').trim())
        .filter(Boolean);
      medicoPayload.append('idiomas', JSON.stringify(idiomasToSend));

      // 3) PATCH de médico
      await axiosInstance.patch(`/medicos/${medicoId}/`, medicoPayload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });  

      toast.success('¡Perfil actualizado con éxito!');
      await refreshAuthData();
      setIsEditing(false);
      setProfilePicFile(null);
      setFirmaDigitalFile(null);
    } catch (error) {
      toast.error('Error al guardar los cambios.');
      console.error(error.response?.data || error);
    } finally {
      setSaving(false);
    }
  };

  // Componente para el selector de código de país
  const PhoneCodeSelector = ({ value, onChange, disabled = false, label = "Código" }) => {
    const selectedOption = phoneCodes.find(option => option.code === value) || { code: value, country: '' };

    return (
      <Autocomplete
        value={selectedOption}
        onChange={(event, newValue) => {
          onChange(newValue?.code || '+504');
        }}
        options={phoneCodes}
        getOptionLabel={(option) => `${option.code} ${option.country}`}
        disabled={disabled || phoneCodesLoading}
        loading={phoneCodesLoading}
        sx={{
          minWidth: { xs: 110, sm: 140 },
          mr: 1,
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            variant="outlined"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {phoneCodesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <Box component="li" {...props} key={option.id}>
            <Typography variant="body2">
              {option.code} {option.country}
            </Typography>
          </Box>
        )}
        ListboxProps={{
          style: { maxHeight: 300 }
        }}
        slotProps={{
          popper: {
            sx: {
              width: 'fit-content !important',
              minWidth: '300px !important',
            }
          }
        }}
      />
    );
  };

  // --- Renderizado del Componente ---

  if (loading || !profileData) {
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '80vh',
        px: 2
      }}>
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary', textAlign: 'center' }}>
          Cargando perfil...
        </Typography>
      </Box>
    );
  }

  // Estilos comunes para los Paper (más responsivos)
  const paperStyle = {
    p: { xs: 2, sm: 3 },
    borderRadius: '16px',
    boxShadow: 'none',
    border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
    transition: 'all 0.3s ease',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
      borderColor: alpha(theme.palette.primary.main, 0.2),
    }
  };

  // Genera slug amigable a partir del nombre (sin espacios ni acentos)
  const makeDoctorSlug = (first = '', last = '', genero = '') => {
    const firstToken = (first || '').trim().split(/\s+/)[0] || '';
    const lastToken  = (last || '').trim().split(/\s+/)[0] || '';
    const norm = (s) =>
      (s || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z0-9]/g, '');
    const prefix = genero === 'Femenino' ? 'Dra' : 'Dr';
    return `${prefix}.${norm(firstToken)}${norm(lastToken)}`;
  };

  const normalizeInstagram = (value) => {
    if (!value) return { href: '', label: 'No especificado' };

    // Si viene una URL, extraer usuario; si viene @usuario o usuario, normalizar
    const stripBase = (v) => v
      .replace(/^https?:\/\/(www\.)?instagram\.com\//i, '')
      .replace(/^@/, '')
      .split(/[/?#]/)[0]
      .trim();

    const user = stripBase(value);
    const href = `https://instagram.com/${user}`;
    const label = `@${user}`;
    return { href, label };
  };

  const normalizeLinkedIn = (value, maxLen = 30) => {
    if (!value) return { href: '', label: 'No especificado' };

    // Si viene un path "in/usuario" o similar, convertirlo a URL; si viene URL, usarla
    const toHref = (v) => v.startsWith('http')
      ? v
      : `https://linkedin.com/${v.replace(/^\/+/, '')}`;

    const href = toHref(value);
    const path = href
      .replace(/^https?:\/\/(www\.)?linkedin\.com\//i, '')
      .replace(/\/+$/,'')
      .trim();

    const label = path.length > maxLen ? `${path.slice(0, maxLen)}…` : path;
    return { href, label };
  };

  // Enlace público para agendar con este médico
  const medicoId = user?.medico?.id_medico;
  const canonicalUrl = isDoctor ? `${window.location.origin}/book/${medicoId}` : null;
  const friendlySlug = isDoctor ? makeDoctorSlug(profileData.first_name, profileData.last_name, profileData.genero) : '';
  const friendlyUrl = medicoId ? `${window.location.origin}/book/${friendlySlug}` : '';
  const doctorTitle = `${profileData.genero === 'Femenino' ? 'Dra.' : 'Dr.'} ${profileData.first_name}
  ${profileData.last_name}`;

  const handleCopy = async (text) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.top = '-9999px';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      toast.success('Enlace copiado al portapapeles');
    } catch (e) {
      console.error(e);
      toast.error('No se pudo copiar el enlace');
    }
  };

  const getIdioma = (idx) => (profileData?.idiomas?.[idx] ?? '');
  const setIdioma = (idx, value) => {
    setProfileData(prev => {
      const current = Array.isArray(prev.idiomas) ? [...prev.idiomas] : [];
      // Extender a 2 posiciones
      if (current.length < 2) {
        current[0] = current[0] ?? '';
        current[1] = current[1] ?? '';
      }
      current[idx] = value;
      return { ...prev, idiomas: current };
    });
  };

  return (
    <Box p={{ xs: 2, sm: 3 }} sx={{ maxWidth: '2000px', margin: '0 auto' }}>
      {/* Header estilo FinanzasPage */}
      <Fade in={fadeIn} timeout={800}>
        <MotionPaper
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          elevation={0}
          sx={{
            position: 'relative',
            overflow: 'hidden',
            borderRadius: 4,
            mb: 3,
            px: { xs: 3, md: 5 },
            py: { xs: 3, md: 4 },
            background: (t) =>
              `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.primary.dark} 100%)`,
            color: 'white',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: -50,
              right: -50,
              width: 200,
              height: 200,
              borderRadius: '50%',
              background: (t) => alpha('#fff', 0.1),
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: -30,
              left: -30,
              width: 150,
              height: 150,
              borderRadius: '50%',
              background: (t) => alpha('#fff', 0.08),
            }}
          />

          <Box
            sx={{
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              alignItems: { xs: 'stretch', sm: 'center' },
              justifyContent: 'space-between',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  bgcolor: (t) => alpha('#fff', 0.2),
                  color: 'white',
                  borderRadius: '50%',
                  width: 60,
                  height: 60,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: (t) => `0 8px 32px ${alpha('#000', 0.2)}`,
                  flexShrink: 0,
                }}
              >
                <AccountCircleIcon sx={{ fontSize: 34 }} />
              </Box>
              <Box>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 800, textShadow: '0 2px 10px rgba(0,0,0,0.25)' }}
                >
                  Mi Perfil
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Gestiona tu información profesional y personal
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: { xs: 'stretch', sm: 'flex-end' } }}>
              {isEditing ? (
                <>
                  <Button
                    variant="outlined"
                    color="inherit"
                    startIcon={<CancelIcon />}
                    onClick={handleCancel}
                    size="small"
                    sx={{
                      borderRadius: '20px',
                      px: 2.5,
                      py: 1,
                      flex: { xs: '1 1 100%', sm: '0 0 auto' },
                      bgcolor: alpha('#fff', 0.08),
                      borderColor: alpha('#fff', 0.3),
                      color: 'white',
                      '&:hover': { bgcolor: alpha('#fff', 0.15), borderColor: alpha('#fff', 0.5) },
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SaveIcon />}
                    onClick={() => setConfirmOpen(true)}
                    size="small"
                    sx={{
                      flex: { xs: '1 1 100%', sm: '0 0 auto' },
                      borderRadius: '20px',
                      px: 2.5,
                      py: 1.1,
                      bgcolor: 'white',
                      color: 'primary.main',
                      fontWeight: 'bold',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                      '&:hover': {
                        bgcolor: 'grey.100',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 25px rgba(0,0,0,0.2)',
                      },
                      transition: 'all 0.25s ease',
                    }}
                  >
                    Guardar Cambios
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<CalendarMonthIcon />}
                    onClick={() => navigate('/doctor/disponibilidad')}
                    size="small"
                    sx={{
                      flex: { xs: '1 1 100%', sm: '0 0 auto' },
                      borderRadius: '20px',
                      px: 2.5,
                      py: 1.1,
                      bgcolor: 'white',
                      color: 'primary.main',
                      fontWeight: 'bold',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                      '&:hover': {
                        bgcolor: 'grey.100',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 25px rgba(0,0,0,0.2)',
                      },
                      transition: 'all 0.25s ease',
                    }}
                  >
                    Disponibilidad
                  </Button>
                  <Button
                    variant="outlined"
                    color="inherit"
                    startIcon={<RateReviewIcon />}
                    onClick={() => navigate('/doctor/opiniones')}
                    size="small"
                    sx={{
                      flex: { xs: '1 1 100%', sm: '0 0 auto' },
                      borderRadius: '20px',
                      px: 2.5,
                      py: 1,
                      bgcolor: alpha('#fff', 0.08),
                      borderColor: alpha('#fff', 0.3),
                      color: 'white',
                      '&:hover': { bgcolor: alpha('#fff', 0.15), borderColor: alpha('#fff', 0.5) },
                    }}
                  >
                    Opiniones
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => setIsEditing(true)}
                    color="inherit"
                    size="small"
                    sx={{
                      flex: { xs: '1 1 100%', sm: '0 0 auto' },
                      borderRadius: '20px',
                      px: 2.5,
                      py: 1,
                      bgcolor: alpha('#fff', 0.08),
                      borderColor: alpha('#fff', 0.3),
                      color: 'white',
                      '&:hover': { bgcolor: alpha('#fff', 0.15), borderColor: alpha('#fff', 0.5) },
                    }}
                  >
                    Editar Perfil
                  </Button>
                </>
              )}
            </Box>
          </Box>
        </MotionPaper>
      </Fade>

      {/* Layout principal */}
      <Grid container spacing={4} justifyContent="center">
        <Grid xs={12} md={11} lg={10}>
          {/* Sección superior (Avatar y Sobre Mí) */}
          <Fade in={fadeIn} timeout={1000}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2.5, md: 4 }, mb: 4 }}>
              {/* Avatar */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  minWidth: { xs: '100%', md: '250px' }
                }}
              >
                <Box
                  position="relative"
                  sx={{
                    mb: 2,
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: isEditing ? 'scale(1.05)' : 'none',
                    }
                  }}
                >
                  <Avatar
                    src={previewImage}
                    alt={`${profileData.first_name} ${profileData.last_name}`}
                    onError={(e) => {
                      e.target.onerror = null;
                      setPreviewImage(null);
                    }}
                    sx={{
                      width: { xs: 140, sm: 160, md: 180 },
                      height: { xs: 140, sm: 160, md: 180 },
                      border: '4px solid',
                      borderColor: 'primary.main',
                      boxShadow: theme.shadows[4],
                      bgcolor: 'primary.light',
                      fontSize: { xs: '2.5rem', sm: '3.25rem', md: '4rem' },
                    }}
                  >
                    {profileData.first_name?.[0]}{profileData.last_name?.[0]}
                  </Avatar>
                  {isEditing && (
                    <IconButton
                      component="label"
                      sx={{
                        position: 'absolute',
                        bottom: 8,
                        right: 8,
                        backgroundColor: 'primary.main',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'primary.dark',
                          transform: 'scale(1.08)'
                        },
                        transition: 'all 0.2s ease',
                        width: 38,
                        height: 38,
                      }}
                    >
                      <PhotoCamera />
                      <input type="file" hidden accept="image/*" onChange={handleFileChange} />
                    </IconButton>
                  )}
                </Box>

                {/* Resumen */}
                <Card
                  variant="outlined"
                  sx={{
                    width: '100%',
                    borderRadius: '16px',
                    boxShadow: 'none',
                    border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                    overflow: 'visible'
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                    <Typography variant="h6" align="center" gutterBottom sx={{ fontSize: { xs: '1.05rem', sm: '1.15rem' } }}>
                      {profileData.genero === 'Femenino' ? 'Dra. ' : 'Dr. '}
                      {profileData.first_name} {profileData.last_name}
                    </Typography>

                    <Chip
                      label={profileData.especialidad_principal || "Sin especialidad"}
                      color="primary"
                      sx={{
                        display: 'block',
                        mx: 'auto',
                        mb: 1.5,
                        fontWeight: 'medium',
                        minWidth: '120px',
                        height: 'auto',
                        '& .MuiChip-label': {
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '100%',
                          py: 0.75,
                          px: 2,
                          whiteSpace: 'normal',
                          lineHeight: 1.5,
                          textAlign: 'center'
                        }
                      }}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <LocalHospitalIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }} />
                      <Typography variant="body2" color="text.secondary">
                        {profileData.numero_colegiado || "No especificado"}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <LocationOnIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }} />
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {profileData.ciudad ? `${profileData.ciudad}, ${profileData.pais}` : "Ubicación no especificada"}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <SchoolIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }} />
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {profileData.universidad || "Universidad no especificada"}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Box>

              {/* Sobre Mí */}
              <Box sx={{ flexGrow: 1, width: '100%' }}>
                <Paper
                  variant="outlined"
                  sx={paperStyle}
                >
                  {isEditing ? (
                    <TextField
                      fullWidth
                      multiline
                      minRows={isXs ? 6 : 10}
                      label="Sobre Mí"
                      name="sobre_mi"
                      value={profileData.sobre_mi}
                      onChange={handleChange}
                      inputProps={{ maxLength: 1500 }}
                      InputProps={{
                        readOnly: false,
                        sx: { alignItems: 'flex-start' },
                      }}
                      variant="outlined"
                      size="small"
                      helperText={`${profileData.sobre_mi?.length || 0}/1500 caracteres`}
                    />
                  ) : (
                    <>
                      <Typography
                        variant="h6"
                        color="primary"
                        gutterBottom
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          borderBottom: '2px solid',
                          borderColor: alpha(theme.palette.primary.main, 0.2),
                          pb: 1,
                          mb: 2
                        }}
                      >
                        Sobre Mí
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          whiteSpace: 'pre-line',
                          minHeight: 150,
                          textAlign: 'justify',
                          fontStyle: profileData.sobre_mi ? 'normal' : 'italic',
                          color: profileData.sobre_mi ? 'text.primary' : 'text.disabled',
                          lineHeight: 1.8,
                          px: 0.5
                        }}
                      >
                        {profileData.sobre_mi || 'Este médico aún no ha escrito una descripción.'}
                      </Typography>
                    </>
                  )}
                </Paper>
              </Box>
            </Box>
          </Fade>

          {/* Resto de información */}
          <Grid container spacing={4}>
            {/* Información Personal */}
            <Grid xs={12}>
              <Fade in={fadeIn} timeout={1200}>
                <Paper
                  variant="outlined"
                  sx={paperStyle}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar
                      sx={{
                        width: 34,
                        height: 34,
                        mr: 1.5,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main
                      }}
                    >
                      <EmailIcon fontSize="small" />
                    </Avatar>
                    <Typography variant="h6" gutterBottom sx={{ mb: 0, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                      Información Personal
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 3 }} />
                  <Grid container spacing={2}>
                    <Grid xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Nombres"
                        name="first_name"
                        value={profileData.first_name}
                        onChange={handleChange}
                        size="small"
                        InputProps={{
                          readOnly: !isEditing
                        }}
                      />
                    </Grid>
                    <Grid xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Apellidos"
                        name="last_name"
                        value={profileData.last_name}
                        onChange={handleChange}
                        size="small"
                        InputProps={{
                          readOnly: !isEditing
                        }}
                      />
                    </Grid>
                    <Grid xs={12} sm={6}>
                      {isEditing ? (
                        <FormControl fullWidth size="small">
                          <InputLabel id="genero-label">Género</InputLabel>
                          <Select
                            labelId="genero-label"
                            id="genero"
                            name="genero"
                            value={profileData.genero || ''}
                            onChange={handleChange}
                            label="Género"
                          >
                            <MenuItem value="">
                              <em>No especificado</em>
                            </MenuItem>
                            <MenuItem value="Masculino">Masculino</MenuItem>
                            <MenuItem value="Femenino">Femenino</MenuItem>
                          </Select>
                        </FormControl>
                      ) : (
                        <TextField
                          fullWidth
                          label="Género"
                          value={profileData.genero || 'No especificado'}
                          size="small"
                          InputProps={{
                            readOnly: true
                          }}
                        />
                      )}
                    </Grid>
                    <Grid xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Correo Electrónico"
                        name="email"
                        value={profileData.email ?? ''}
                        onChange={handleChange}
                        size="small"
                        InputProps={{
                          readOnly: !isEditing
                        }}
                        error={!!errors.email}
                        helperText={errors.email}
                      />
                    </Grid>
                    <Grid xs={12} sm={6}>
                      {isEditing ? (
                        <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, alignItems: 'flex-start' }}>
                          <PhoneCodeSelector
                            value={celularCode}
                            onChange={setCelularCode}
                            disabled={phoneCodesLoading}
                          />
                          <TextField
                            fullWidth
                            label="Celular"
                            name="celular"
                            value={profileData.celular}
                            onChange={handleChange}
                            size="small"
                            error={!!errors.celular}
                            helperText={errors.celular}
                          />
                        </Box>
                      ) : (
                        <TextField
                          fullWidth
                          label="Celular"
                          value={`${celularCode} ${profileData.celular}`}
                          size="small"
                          InputProps={{
                            readOnly: true
                          }}
                        />
                      )}
                    </Grid>
                  </Grid>
                </Paper>
              </Fade>
            </Grid>

            {/* Datos Profesionales */}
            <Grid xs={12}>
              <Fade in={fadeIn} timeout={1400}>
                <Paper
                  variant="outlined"
                  sx={paperStyle}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar
                      sx={{
                        width: 34,
                        height: 34,
                        mr: 1.5,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main
                      }}
                    >
                      <LocalHospitalIcon fontSize="small" />
                    </Avatar>
                    <Typography variant="h6" gutterBottom sx={{ mb: 0, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                      Datos Profesionales
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 3 }} />
                  <Grid container spacing={2}>
                    <Grid xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Especialidad Principal"
                        name="especialidad_principal"
                        value={profileData.especialidad_principal}
                        onChange={handleChange}
                        size="small"
                        InputProps={{
                          readOnly: !isEditing
                        }}
                      />
                    </Grid>
                    <Grid xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Subespecialidad|Diplomados"
                        name="sub_especialidad"
                        value={profileData.sub_especialidad}
                        onChange={handleChange}
                        size="small"
                        InputProps={{
                          readOnly: !isEditing
                        }}
                      />
                    </Grid>
                    <Grid xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Número de Colegiado"
                        name="numero_colegiado"
                        value={profileData.numero_colegiado}
                        onChange={handleChange}
                        size="small"
                        InputProps={{
                          readOnly: !isEditing
                        }}
                      />
                    </Grid>
                    <Grid xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Universidad"
                        name="universidad"
                        value={profileData.universidad}
                        onChange={handleChange}
                        size="small"
                        InputProps={{
                          readOnly: !isEditing
                        }}
                      />
                    </Grid>
                    {isEditing ? (
                      <>
                        <Grid xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Idioma 1"
                            value={getIdioma(0)}
                            onChange={(e) => setIdioma(0, e.target.value)}
                            placeholder="Ej. Español"
                            size="small"
                          />
                        </Grid>
                        <Grid xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Idioma 2"
                            value={getIdioma(1)}
                            onChange={(e) => setIdioma(1, e.target.value)}
                            placeholder="Ej. Inglés"
                            helperText="Puedes dejar vacío cualquiera de los dos"
                            size="small"
                          />
                        </Grid>
                      </>
                    ) : (
                      <Grid xs={12}>
                        <TextField
                          fullWidth
                          label="Idiomas"
                          value={
                            (Array.isArray(profileData.idiomas) && profileData.idiomas.filter(Boolean).join(', ')) ||
                            'No especificado'
                          }
                          size="small"
                          InputProps={{ readOnly: true }}
                        />
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              </Fade>
            </Grid>

            {/* Ubicación y Consultorio */}
            <Grid xs={12}>
              <Fade in={fadeIn} timeout={1600}>
                <Paper variant="outlined" sx={paperStyle}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar
                      sx={{
                        width: 34,
                        height: 34,
                        mr: 1.5,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main
                      }}
                    >
                      <LocationOnIcon fontSize="small" />
                    </Avatar>
                    <Typography variant="h6" gutterBottom sx={{ mb: 0, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                      Ubicación y Consultorio
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 3 }} />
                  <Grid container spacing={2}>
                    <Grid xs={12} sm={6}>
                      {isEditing ? (
                        <Autocomplete
                          fullWidth
                          options={countries}
                          getOptionLabel={(option) => option.name || ''}
                          value={countries.find(c => c.name === profileData.pais) || null}
                          onChange={handleCountryChange}
                          ListboxProps={{
                            style: { maxHeight: '300px' }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="País"
                              fullWidth
                              size="small"
                            />
                          )}
                          renderOption={(props, option) => (
                            <MenuItem {...props} key={option.code2 || option.name} sx={{ py: 1.25, px: 2 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                {option.code2 && (
                                  <img
                                    loading="lazy"
                                    width="26"
                                    src={`https://flagcdn.com/w20/${option.code2.toLowerCase()}.png`}
                                    alt=""
                                    style={{ marginRight: '12px' }}
                                  />
                                )}
                                <Typography variant="body1">{option.name}</Typography>
                              </Box>
                            </MenuItem>
                          )}
                          slotProps={{
                            popper: {
                              sx: {
                                width: 'fit-content !important',
                                minWidth: '350px !important',
                                '& .MuiPaper-root': {
                                  width: '100%',
                                }
                              }
                            }
                          }}
                        />
                      ) : (
                        <TextField
                          fullWidth
                          label="País"
                          value={profileData.pais || 'No especificado'}
                          size="small"
                          InputProps={{
                            readOnly: true
                          }}
                        />
                      )}
                    </Grid>
                    <Grid xs={12} sm={6}>
                      {isEditing ? (
                        <Autocomplete
                          fullWidth
                          options={states}
                          getOptionLabel={(option) => option.name || ''}
                          value={states.find(s => s.name === profileData.estado_provincia) || null}
                          onChange={(e, newValue) => {
                            setProfileData(prev => ({
                              ...prev,
                              estado_provincia: newValue?.name || ''
                            }));
                          }}
                          loading={stateLoading}
                          disabled={!profileData.pais || states.length === 0}
                          ListboxProps={{
                            style: { maxHeight: '300px' }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Estado/Provincia"
                              fullWidth
                              size="small"
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {stateLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                          renderOption={(props, option) => (
                            <MenuItem {...props} key={option.code || option.name} sx={{ py: 1.25, px: 2 }}>
                              <Typography variant="body1">{option.name}</Typography>
                            </MenuItem>
                          )}
                          slotProps={{
                            popper: {
                              sx: {
                                width: 'fit-content !important',
                                minWidth: '350px !important',
                                '& .MuiPaper-root': {
                                  width: '100%',
                                }
                              }
                            }
                          }}
                        />
                      ) : (
                        <TextField
                          fullWidth
                          label="Estado/Provincia"
                          value={profileData.estado_provincia || 'No especificado'}
                          size="small"
                          InputProps={{
                            readOnly: true
                          }}
                        />
                      )}
                    </Grid>
                    <Grid xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Ciudad"
                        name="ciudad"
                        value={profileData.ciudad}
                        onChange={handleChange}
                        size="small"
                        InputProps={{
                          readOnly: !isEditing
                        }}
                      />
                    </Grid>
                    <Grid xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Dirección Personal"
                        name="direccion"
                        value={profileData.direccion}
                        onChange={handleChange}
                        size="small"
                        InputProps={{
                          readOnly: !isEditing
                        }}
                      />
                    </Grid>
                    <Grid xs={12} sm={6}>
                      {isEditing ? (
                        <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, alignItems: 'flex-start' }}>
                          <PhoneCodeSelector
                            value={consultorioCode}
                            onChange={setConsultorioCode}
                            disabled={phoneCodesLoading}
                          />
                          <TextField
                            fullWidth
                            label="Teléfono Consultorio"
                            name="telefono_consultorio"
                            value={profileData.telefono_consultorio}
                            onChange={handleChange}
                            size="small"
                          />
                        </Box>
                      ) : (
                        <TextField
                          fullWidth
                          label="Teléfono Consultorio"
                          value={`${consultorioCode} ${profileData.telefono_consultorio}`}
                          size="small"
                          InputProps={{
                            readOnly: true
                          }}
                        />
                      )}
                    </Grid>
                    <Grid xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Dirección Consultorio"
                        name="direccion_consultorio"
                        value={profileData.direccion_consultorio}
                        onChange={handleChange}
                        size="small"
                        InputProps={{
                          readOnly: !isEditing
                        }}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Fade>
            </Grid>

            {isDoctor && (
              <Grid xs={12}>
                <Fade in={fadeIn} timeout={1500}>
                  <Paper variant="outlined" sx={{ ...paperStyle }}>
                    {/* ... */}
                    <Box sx={{ mb: 2, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, alignItems: { xs: 'stretch', sm: 'center' } }}>
                      <TextField
                        fullWidth
                        label="Enlace para agendar (amigable)"
                        value={friendlyUrl || ''}
                        size="small"
                        InputProps={{
                          readOnly: true,
                          endAdornment: friendlyUrl ? (
                            <IconButton aria-label="Copiar enlace amigable" onClick={() => handleCopy(friendlyUrl)} edge="end">
                              <ContentCopyIcon />
                            </IconButton>
                          ) : null,
                        }}
                        helperText="Comparte este enlace con tus pacientes."
                      />
                      {friendlyUrl && (
                        <Button
                          variant="outlined"
                          href={friendlyUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ whiteSpace: 'nowrap', flex: { xs: '1 1 100%', sm: '0 0 auto' } }}
                          size="small"
                        >
                          Ver página
                        </Button>
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, alignItems: { xs: 'stretch', sm: 'center' } }}>
                      <TextField
                        fullWidth
                        label="Enlace canónico (ID)"
                        value={canonicalUrl || ''}
                        size="small"
                        InputProps={{
                          readOnly: true,
                          endAdornment: canonicalUrl ? (
                            <IconButton aria-label="Copiar enlace canónico" onClick={() => handleCopy(canonicalUrl)} edge="end">
                              <ContentCopyIcon />
                            </IconButton>
                          ) : null,
                        }}
                        helperText="Enlace directo con ID (útil como respaldo)."
                      />
                      {canonicalUrl && (
                        <Button
                          variant="outlined"
                          href={canonicalUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ whiteSpace: 'nowrap', flex: { xs: '1 1 100%', sm: '0 0 auto' } }}
                          size="small"
                        >
                          Abrir enlace ID
                        </Button>
                      )}
                    </Box>
                  </Paper>
                </Fade>
              </Grid>
            )}


            {/* Configuración de Pagos */}
            {isDoctor && (
            <Grid xs={12}>
              <Fade in={fadeIn} timeout={1500}>
                <Paper variant="outlined" sx={{ ...paperStyle }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{
                      width: 34, height: 34, mr: 1.5,
                      bgcolor: alpha(theme.palette.success.main, 0.1),
                      color: theme.palette.success.main
                    }}>
                      <PaymentIcon fontSize="small" />
                    </Avatar>
                    <Typography variant="h6" gutterBottom sx={{ mb: 0, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                      Configuración de Pagos
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />

                  {/* Disclaimer Business PayPal */}
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Para recibir pagos, el correo de PayPal debe pertenecer a una cuenta PayPal Business.
                  </Alert>

                  {loadingPayConfig ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <CircularProgress size={24} />
                      <Typography>Cargando configuración...</Typography>
                    </Box>
                  ) : (
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={!!paymentConfig.permitir_agendamiento_online}
                              onChange={(_, checked) =>
                                setPaymentConfig(p => ({ ...p, permitir_agendamiento_online: checked }))
                              }
                              color="primary"
                              size="small"
                            />
                          }
                          label={paymentConfig.permitir_agendamiento_online ? 'Agendamiento online: Activado' : 'Agendamiento online: Desactivado'}
                          sx={{ mb: 1 }}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Correo de PayPal (receptor)"
                          value={paymentConfig.cuenta_paypal ?? ''}
                          onChange={(e) => setPaymentConfig(p => ({ ...p, cuenta_paypal: e.target.value }))}
                          placeholder="ejemplo@paypal.com"
                          helperText={isEditing ? "Debe ser un correo de una cuenta PayPal Business" : "Activa el modo edición del perfil para cambiar este correo"}
                          disabled={!isEditing}
                          size="small"
                        />
                      </Grid>

                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          fullWidth
                          label="Precio de consulta"
                          type="number"
                          value={paymentConfig.tarifa_consulta}
                          onChange={(e) => setPaymentConfig(p => ({ ...p, tarifa_consulta: e.target.value }))}
                          inputProps={{ min: 0, step: '0.01' }}
                          size="small"
                        />
                      </Grid>

                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small">
                          <InputLabel id="moneda-label">Moneda</InputLabel>
                          <Select
                            labelId="moneda-label"
                            label="Moneda"
                            value={paymentConfig.moneda}
                            onChange={(e) => setPaymentConfig(p => ({ ...p, moneda: e.target.value }))}
                          >
                            {currencyOptions.map((c) => (
                              <MenuItem key={c} value={c}>{c}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Chip
                            label={paymentConfig.permitir_agendamiento_online ? 'Agendamiento online: Activado' : 'Agendamiento online: Desactivado'}
                            color={paymentConfig.permitir_agendamiento_online ? 'success' : 'default'}
                            variant="outlined"
                            size="small"
                          />

                          <Button
                            variant="outlined"
                            onClick={() => {
                              setLoadingPayConfig(true);
                              (async () => {
                                try {
                                  const { data } = await axiosInstance.get('/medicos/configuracion-pago/', {
                                    params: { t: Date.now() }
                                  });
                                  setPaymentConfig({
                                    cuenta_paypal: data.cuenta_paypal || '',
                                    tarifa_consulta: data.tarifa_consulta ?? 0,
                                    moneda: data.moneda || 'USD',
                                    permitir_agendamiento_online:
                                      data.permitir_agendamiento_online ?? true,
                                  });
                                } catch (e) {
                                  if (e?.response?.status === 404) {
                                    toast.error('Endpoint de configuración no encontrado en el servidor.');
                                  } else {
                                    toast.error('No se pudo recargar la configuración desde el servidor.');
                                  }
                                } finally {
                                  setLoadingPayConfig(false);
                                }
                              })();
                            }}
                            size="small"
                            sx={{ flex: { xs: '1 1 100%', sm: '0 0 auto' } }}
                          >
                            Deshacer cambios
                          </Button>

                          <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSavePayConfig}
                            startIcon={savingPayConfig ? <CircularProgress size={16} /> : null}
                            disabled={savingPayConfig}
                            size="small"
                            sx={{ flex: { xs: '1 1 100%', sm: '0 0 auto' } }}
                          >
                            Guardar Configuración
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  )}
                </Paper>
              </Fade>
            </Grid>
            )}

            {/* DIVISOR VISUAL */}
            <Box
              sx={{
                width: '100%',
                mt: 6,
                mb: 3,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                px: 1
              }}
            >
              <Divider sx={{ flexGrow: 1 }} />
              <Chip
                label="Presencia Digital"
                color="secondary"
                variant="outlined"
                size="medium"
                sx={{
                  fontWeight: 'medium',
                  px: 1.5,
                  borderWidth: 1.5,
                  backgroundColor: alpha(theme.palette.secondary.main, 0.05),
                  borderColor: alpha(theme.palette.secondary.main, 0.3),
                  color: theme.palette.secondary.main
                }}
              />
              <Divider sx={{ flexGrow: 1 }} />
            </Box>

            {/* Redes Sociales */}
            <Grid xs={12}>
              <Fade in={fadeIn} timeout={1800}>
                <Paper
                  variant="outlined"
                  sx={{
                    ...paperStyle,
                    borderColor: alpha(theme.palette.secondary.main, 0.2),
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                      borderColor: alpha(theme.palette.secondary.main, 0.4),
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar
                      sx={{
                        width: 34,
                        height: 34,
                        mr: 1.5,
                        bgcolor: alpha(theme.palette.secondary.main, 0.1),
                        color: theme.palette.secondary.main
                      }}
                    >
                      <PublicIcon fontSize="small" />
                    </Avatar>
                    <Typography variant="h6" gutterBottom sx={{ mb: 0, color: theme.palette.info.main, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                      Redes Sociales
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 3 }} />
                  {isEditing && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                      Ingresa las URLs completas de tus perfiles en redes sociales
                    </Typography>
                  )}

                  <Grid container spacing={{ xs: 2, sm: 3 }}>
                    {/* Instagram */}
                    <Grid item xs={12} sm={6}>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          label="Instagram"
                          name="instagram"
                          value={profileData.instagram}
                          onChange={handleChange}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <InstagramIcon color="action" />
                              </InputAdornment>
                            ),
                            sx: {
                              px: { xs: 1, sm: 2 }
                            }
                          }}
                          placeholder="@usuario o URL completa"
                          size="small"
                          sx={{
                            '& .MuiInputBase-root': {
                              height: { xs: '45px', sm: 'auto' }
                            }
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            p: { xs: 1.5, sm: 1 },
                            borderRadius: 2,
                            '&:hover': {
                              bgcolor: 'action.hover',
                            },
                            transition: 'background-color 0.2s'
                          }}
                        >
                          <Avatar
                            sx={{
                              bgcolor: '#E1306C',
                              width: { xs: 36, sm: 32 },
                              height: { xs: 36, sm: 32 },
                              mr: 2
                            }}
                          >
                            <InstagramIcon sx={{ color: 'white', fontSize: { xs: 20, sm: 18 } }} />
                          </Avatar>
                          {/* Instagram (modo lectura) */}
                          <Box sx={{ overflow: 'hidden', flexGrow: 1 }}>
                            <Typography variant="body2" color="text.secondary" noWrap>
                              Instagram
                            </Typography>
                            {profileData.instagram ? (
                              (() => {
                                const ig = normalizeInstagram(profileData.instagram);
                                return (
                                  <Link
                                    href={ig.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    underline="hover"
                                    color="primary"
                                    title={ig.href}
                                    sx={{
                                      display: 'block',
                                      fontWeight: 'medium',
                                      fontSize: { xs: '0.9rem', sm: '0.875rem' },
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap'
                                    }}
                                  >
                                    {ig.label}
                                  </Link>
                                );
                              })()
                            ) : (
                              <Typography variant="body2" color="text.disabled">No especificado</Typography>
                            )}
                          </Box>
                        </Box>
                      )}
                    </Grid>

                    {/* Facebook */}
                    <Grid item xs={12} sm={6}>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          label="Facebook"
                          name="facebook"
                          value={profileData.facebook}
                          onChange={handleChange}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <FacebookIcon color="action" />
                              </InputAdornment>
                            ),
                            sx: {
                              px: { xs: 1, sm: 2 }
                            }
                          }}
                          placeholder="@usuario o URL completa"
                          size="small"
                          sx={{
                            '& .MuiInputBase-root': {
                              height: { xs: '45px', sm: 'auto' }
                            }
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            p: { xs: 1.5, sm: 1 },
                            borderRadius: 2,
                            '&:hover': {
                              bgcolor: 'action.hover',
                            },
                            transition: 'background-color 0.2s'
                          }}
                        >
                          <Avatar
                            sx={{
                              bgcolor: '#1877F2',
                              width: { xs: 36, sm: 32 },
                              height: { xs: 36, sm: 32 },
                              mr: 2
                            }}
                          >
                            <FacebookIcon sx={{ color: 'white', fontSize: { xs: 20, sm: 18 } }} />
                          </Avatar>
                          <Box sx={{ overflow: 'hidden', flexGrow: 1 }}>
                            <Typography variant="body2" color="text.secondary" noWrap>
                              Facebook
                            </Typography>
                            {profileData.facebook ? (
                              <Link
                                href={profileData.facebook.startsWith('http') ? profileData.facebook : `https://facebook.com/${profileData.facebook.replace('/', '')}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                underline="hover"
                                color="primary"
                                sx={{
                                  display: 'block',
                                  fontWeight: 'medium',
                                  fontSize: { xs: '0.9rem', sm: '0.875rem' },
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {profileData.facebook.startsWith('http')
                                  ? profileData.facebook.replace(/https?:\/\/(www\.)?facebook\.com\//, '')
                                  : profileData.facebook
                                }
                              </Link>
                            ) : (
                              <Typography variant="body2" color="text.disabled">No especificado</Typography>
                            )}
                          </Box>
                        </Box>
                      )}
                    </Grid>

                    {/* TikTok */}
                    <Grid item xs={12} sm={6}>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          label="TikTok"
                          name="tiktok"
                          value={profileData.tiktok}
                          onChange={handleChange}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Box
                                  component="span"
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 24,
                                    height: 24,
                                    color: 'action.active'
                                  }}
                                >
                                  <Typography variant="body2" fontWeight="bold">TT</Typography>
                                </Box>
                              </InputAdornment>
                            ),
                            sx: {
                              px: { xs: 1, sm: 2 }
                            }
                          }}
                          placeholder="@usuario o URL completa"
                          size="small"
                          sx={{
                            '& .MuiInputBase-root': {
                              height: { xs: '45px', sm: 'auto' }
                            }
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            p: { xs: 1.5, sm: 1 },
                            borderRadius: 2,
                            '&:hover': {
                              bgcolor: 'action.hover',
                            },
                            transition: 'background-color 0.2s'
                          }}
                        >
                          <Avatar
                            sx={{
                              bgcolor: '#000000',
                              width: { xs: 36, sm: 32 },
                              height: { xs: 36, sm: 32 },
                              mr: 2
                            }}
                          >
                            <Typography variant="body2" sx={{ color: 'white', fontWeight: 'bold' }}>TT</Typography>
                          </Avatar>
                          <Box sx={{ overflow: 'hidden', flexGrow: 1 }}>
                            <Typography variant="body2" color="text.secondary" noWrap>
                              TikTok
                            </Typography>
                            {profileData.tiktok ? (
                              <Link
                                href={profileData.tiktok.startsWith('http') ? profileData.tiktok : `https://tiktok.com/@${profileData.tiktok.replace('@', '')}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                underline="hover"
                                color="primary"
                                sx={{
                                  display: 'block',
                                  fontWeight: 'medium',
                                  fontSize: { xs: '0.9rem', sm: '0.875rem' },
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {profileData.tiktok.startsWith('http')
                                  ? profileData.tiktok.replace(/https?:\/\/(www\.)?tiktok\.com\//, '')
                                  : profileData.tiktok.startsWith('@')
                                    ? profileData.tiktok
                                    : `@${profileData.tiktok}`
                                }
                              </Link>
                            ) : (
                              <Typography variant="body2" color="text.disabled">No especificado</Typography>
                            )}
                          </Box>
                        </Box>
                      )}
                    </Grid>

                    {/* LinkedIn */}
                    <Grid item xs={12} sm={6}>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          label="LinkedIn"
                          name="linkedin"
                          value={profileData.linkedin}
                          onChange={handleChange}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <LinkedInIcon color="action" />
                              </InputAdornment>
                            ),
                            sx: {
                              px: { xs: 1, sm: 2 }
                            }
                          }}
                          placeholder="URL completa o /in/usuario"
                          size="small"
                          sx={{
                            '& .MuiInputBase-root': {
                              height: { xs: '45px', sm: 'auto' }
                            }
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            p: { xs: 1.5, sm: 1 },
                            borderRadius: 2,
                            '&:hover': {
                              bgcolor: 'action.hover',
                            },
                            transition: 'background-color 0.2s'
                          }}
                        >
                          <Avatar
                            sx={{
                              bgcolor: '#0A66C2',
                              width: { xs: 36, sm: 32 },
                              height: { xs: 36, sm: 32 },
                              mr: 2
                            }}
                          >
                            <LinkedInIcon sx={{ color: 'white', fontSize: { xs: 20, sm: 18 } }} />
                          </Avatar>
                          {/* LinkedIn (modo lectura) */}
                          <Box sx={{ overflow: 'hidden', flexGrow: 1 }}>
                            <Typography variant="body2" color="text.secondary" noWrap>
                              LinkedIn
                            </Typography>
                            {profileData.linkedin ? (
                              (() => {
                                const ln = normalizeLinkedIn(profileData.linkedin, 32); // puedes ajustar el máximo
                                return (
                                  <Link
                                    href={ln.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    underline="hover"
                                    color="primary"
                                    title={ln.href}
                                    sx={{
                                      display: 'block',
                                      fontWeight: 'medium',
                                      fontSize: { xs: '0.9rem', sm: '0.875rem' },
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap'
                                    }}
                                  >
                                    {ln.label}
                                  </Link>
                                );
                              })()
                            ) : (
                              <Typography variant="body2" color="text.disabled">No especificado</Typography>
                            )}
                          </Box>
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                </Paper>
              </Fade>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* DIVISOR VISUAL */}
      <Box
        sx={{
          width: '100%',
          mt: 6,
          mb: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}
      >
        <Divider sx={{ flexGrow: 1 }} />
        <Chip
          label="Firma Digital"
          color="secondary"
          variant="outlined"
          size="medium"
          sx={{
            fontWeight: 'medium',
            px: 1.5,
            borderWidth: 1.5,
            backgroundColor: alpha(theme.palette.secondary.main, 0.05),
            borderColor: alpha(theme.palette.secondary.main, 0.3),
            color: theme.palette.secondary.main
          }}
        />
        <Divider sx={{ flexGrow: 1 }} />
      </Box>

      {/* Firma Digital y Sello */}
      <Grid xs={12}>
        <Fade in={fadeIn} timeout={2000}>
          <Paper
            variant="outlined"
            sx={{
              ...paperStyle,
              mt: 2,
              borderColor: alpha(theme.palette.primary.main, 0.2),
              '&:hover': {
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                borderColor: alpha(theme.palette.primary.main, 0.4),
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar
                sx={{
                  width: 34,
                  height: 34,
                  mr: 1.5,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main
                }}
              >
                <EditIcon fontSize="small" />
              </Avatar>
              <Typography variant="h6" gutterBottom sx={{ mb: 0, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                Firma y Sello Digital
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />

            <Typography
              variant="body2"
              color={isDarkMode ? "text.primary" : "text.secondary"}
              paragraph
              sx={{ opacity: isDarkMode ? 0.9 : 1 }}
            >
              Estos elementos se usarán en sus documentos oficiales y recetas médicas.
            </Typography>

            <Grid container spacing={3} alignItems="center">
              {/* Controles de subida (izquierda) */}
              <Grid item xs={12} md={6}>
                {isEditing ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* Subir firma */}
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<PhotoCamera />}
                      color="primary"
                      sx={{
                        width: 'fit-content',
                        mb: 1,
                        borderRadius: '8px',
                        py: 1
                      }}
                      size="small"
                    >
                      {firmaDigitalFile ? "Cambiar imagen de firma" : "Subir firma digital"}
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={handleFirmaDigitalChange}
                      />
                    </Button>

                    {firmaDigitalFile && (
                      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                        <Typography
                          variant="body2"
                          color={isDarkMode ? "success.light" : "success.main"}
                          sx={{ mr: 1 }}
                        >
                          Imagen de firma seleccionada: {firmaDigitalFile.name}
                        </Typography>
                        <Button
                          size="small"
                          color="error"
                          onClick={() => {
                            setFirmaDigitalFile(null);
                            setPreviewFirmaDigital(null);
                          }}
                        >
                          Eliminar
                        </Button>
                      </Box>
                    )}

                    {/* Subir sello */}
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<PhotoCamera />}
                      color="secondary"
                      sx={{
                        width: 'fit-content',
                        mb: 1,
                        borderRadius: '8px',
                        py: 1
                      }}
                      size="small"
                    >
                      {selloDigitalFile ? "Cambiar sello" : "Subir sello digital"}
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={handleSelloDigitalChange}
                      />
                    </Button>

                    {selloDigitalFile && (
                      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                        <Typography
                          variant="body2"
                          color={isDarkMode ? "success.light" : "success.main"}
                          sx={{ mr: 1 }}
                        >
                          Sello seleccionado: {selloDigitalFile.name}
                        </Typography>
                        <Button
                          size="small"
                          color="error"
                          onClick={() => {
                            setSelloDigitalFile(null);
                            setPreviewSelloDigital(null);
                          }}
                        >
                          Eliminar
                        </Button>
                      </Box>
                    )}

                    {/* Subir logo de clínica */}
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<PhotoCamera />}
                      color="inherit"
                      sx={{ width: 'fit-content', mb: 1, borderRadius: '8px', py: 1 }}
                      size="small"
                    >
                      {logoClinicaFile ? "Cambiar logo de clínica" : "Subir logo de clínica"}
                      <input type="file" hidden accept="image/*" onChange={handleLogoClinicaChange} />
                    </Button>

                    {logoClinicaFile && (
                      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                        <Typography variant="body2" color={isDarkMode ? "success.light" : "success.main"} sx={{ mr: 1 }}>
                          Logo seleccionado: {logoClinicaFile.name}
                        </Typography>
                        <Button
                          size="small"
                          color="error"
                          onClick={() => {
                            setLogoClinicaFile(null);
                            setPreviewLogoClinica(null);
                          }}
                        >
                          Eliminar
                        </Button>
                      </Box>
                    )}

                    <Typography
                      variant="caption"
                      color={isDarkMode ? "text.primary" : "text.secondary"}
                      sx={{ display: 'block', mt: 1, opacity: 0.8 }}
                    >
                      Recomendación: use imágenes PNG con fondo transparente. Tamaño menor a 1MB. El sello se dibujará aprox. de 3.2 cm en la receta.
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      color={isDarkMode ? "text.primary" : "text.secondary"}
                      gutterBottom
                      sx={{ opacity: isDarkMode ? 0.9 : 1 }}
                    >
                      Su firma digital actual:
                    </Typography>
                    {!previewFirmaDigital && (
                      <Typography
                        variant="body2"
                        color={isDarkMode ? alpha(theme.palette.text.primary, 0.7) : "text.disabled"}
                      >
                        No ha configurado una firma digital. Edite su perfil para añadir una.
                      </Typography>
                    )}

                    <Typography
                      variant="subtitle2"
                      color={isDarkMode ? "text.primary" : "text.secondary"}
                      gutterBottom
                      sx={{ mt: 2, opacity: isDarkMode ? 0.9 : 1 }}
                    >
                      Su sello digital actual:
                    </Typography>
                    {!previewSelloDigital && (
                      <Typography
                        variant="body2"
                        color={isDarkMode ? alpha(theme.palette.text.primary, 0.7) : "text.disabled"}
                      >
                        No ha configurado un sello digital. Edite su perfil para añadir uno.
                      </Typography>
                    )}
                  </Box>
                )}
              </Grid>


              {/* Vista previa (derecha) */}
              <Grid item xs={12} md={6}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    minHeight: 120,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: isDarkMode ? alpha(theme.palette.common.white, 0.05) : alpha(theme.palette.common.black, 0.02),
                    borderStyle: 'dashed',
                    borderRadius: '12px',
                    borderColor: isDarkMode ? alpha(theme.palette.common.white, 0.2) : alpha(theme.palette.common.black, 0.1)
                  }}
                >
                  {(previewFirmaDigital || previewSelloDigital) ? (
                    <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
                      {/* Preview firma */}
                      {previewFirmaDigital && (
                        <Box
                          component="img"
                          src={previewFirmaDigital}
                          alt="Firma Digital"
                          sx={{
                            maxWidth: 260,
                            maxHeight: 150,
                            objectFit: 'contain',
                            borderRadius: 1,
                            filter: isDarkMode && !previewFirmaDigital.includes('.png') ? 'invert(1)' : 'none'
                          }}
                        />
                      )}

                      {/* Preview sello */}
                      {previewSelloDigital && (
                        <Box
                          component="img"
                          src={previewSelloDigital}
                          alt="Sello Digital"
                          sx={{
                            maxWidth: 130,   // aprox. el tamaño objetivo de la receta (~1.3 in)
                            maxHeight: 130,
                            objectFit: 'contain',
                            borderRadius: 1,
                          }}
                        />
                      )}

                      {previewLogoClinica && (
                        <Box
                          component="img"
                          src={previewLogoClinica}
                          alt="Logo de Clínica"
                          sx={{
                            maxWidth: 130,  // similar al sello
                            maxHeight: 130,
                            objectFit: 'contain',
                            borderRadius: 1,
                            border: theme => `1px dashed ${alpha(theme.palette.text.primary, 0.2)}`,
                            p: 1,
                            backgroundColor: theme => alpha(theme.palette.background.paper, 0.4)
                          }}
                        />
                      )}
                    </Box>
                  ) : (
                    <Typography
                      variant="body2"
                      color={isDarkMode ? alpha(theme.palette.common.white, 0.5) : "text.disabled"}
                      align="center"
                    >
                      {isEditing ? 'Vista previa de firma/sello y logo clínica' : 'No hay firma ni sello configurados'}
                    </Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Paper>
        </Fade>
      </Grid>


      {/* Dialog de confirmación */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{
          bgcolor: alpha(theme.palette.primary.main, 0.1),
          color: 'primary.main',
          py: 1.5
        }}>
          Confirmar Guardado
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5, px: 2.5 }}>
          <DialogContentText>
            ¿Estás seguro de que deseas guardar los cambios en tu perfil?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5, backgroundColor: alpha(theme.palette.background.default, 0.4) }}>
          <Button
            onClick={() => setConfirmOpen(false)}
            variant="outlined"
            size="small"
          >
            Cancelar
          </Button>
          <Button
            onClick={confirmSave}
            color="primary"
            variant="contained"
            autoFocus
            size="small"
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Backdrop de guardado */}
      <Backdrop
        open={saving}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 2,
          color: '#fff',
          backdropFilter: 'blur(3px)',
        }}
      >
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          backgroundColor: alpha(theme.palette.background.paper, 0.8),
          p: 3,
          borderRadius: '16px',
          border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
          boxShadow: theme.shadows[4],
          color: theme.palette.text.primary,
          minWidth: 240
        }}>
          <CircularProgress color="primary" size={40} thickness={4} />
          <Typography sx={{ mt: 2, textAlign: 'center' }}>Guardando cambios...</Typography>
        </Box>
      </Backdrop>
    </Box>
  );
};

export default MedicoProfilePage;